import React, { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Grid, } from "@mui/material";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { GET_IMPORT_SHIPMENT_REPORT } from "../../services/apiRoutes";
import { AuthContext } from "../../context/AuthContext";
import { getColorById, getColorPlate, getSuppliersList } from "../../services/CommonServices";
import CustomLoader from "../common/CustomLoader";
import CustomReport from "../common/CustomReport";
import { genericPost } from "../../services/authServices";
import CustomTextField from "../common/CustomTextField";
import CustomFilter from "../common/CustomFilter";
import CustomButton from "../common/CustomButton";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomAutocomplete from "../common/CustomAutoComplete";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomStatus from "../../components/common/CustomStatus";
import { useSelector } from "react-redux";


const title = "Import Shipment Report";

const ImportShipmentReport = () => {
    const colorlist = useSelector((state) => state.common.colorPlate) || [];
    const companyDateFormat = useCompanyDateFormat();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [permission, setPermissions] = useState()
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    // ✅ Pagination states
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);

    const [filterPayload, setFilterPayload] = useState({
        shipmentId: '',
        supplierNamesId: '',
        fromDate: '',
        toDate: '',
    });

    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])

    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: '',
                supplierNamesId: '',
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
    }

    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'date', header: 'Date', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment ID', size: 50, tableStyle: { textAlign: "center" }, },

            {
                accessorKey: 'commercialInvoiceNo',
                header: 'CI Number',
                size: 80,
                tableStyle: { textAlign: "center" },
            },
            {
                accessorKey: 'bookingNumber',
                header: 'Booking Number',
                size: 80,
                tableStyle: { textAlign: "center" },
            },
            {
                accessorKey: 'clientCompanyName', header: 'Supplier Name', size: 50, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },
            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },

            { accessorKey: 'amount', header: 'Amount', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'numberOfContainers', header: 'No. of Containers', size: 50, tableStyle: { textAlign: "center" }, },

        ],
        [permission]
    );
    const fetchData = async (pageIndex = 0, pageSize = 10,) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };

            const response = await genericPost(GET_IMPORT_SHIPMENT_REPORT, payload, {});
            if (response.data.success && response.data.shipments) {
                const shipmentData = response.data.shipments.map((shipment, index) => {
                    const totalAmount = (shipment?.particulars || []).reduce((acc, particular) => {
                        return acc + Number(particular?.amount || 0);
                    }, 0);

                    const countryColorObj = getColorById(
                        shipment?.clientCompany?.billingCountryId,
                        colorlist,
                        {
                            matchKey: "colorId",
                            prefixLength: 1,
                        }
                    ) || {};

                    return {
                        commercialInvoiceId: shipment?.commercialInvoiceId ?? "",
                        index: (index + 1).toString().padStart(2, '0'),
                        date: shipment?.date ? companyDateFormat(shipment.date) : "",
                        shipmentId: shipment?.shipmentId || "",
                        clientCompanyName: shipment?.clientCompany?.clientCompanyName || "",
                        billingCountry: shipment?.clientCompany?.billingCountry?.name || "",
                        amount: `${shipment?.currency?.currency_symbol || ""} ${totalAmount.toFixed(2)}`,
                        numberOfContainers: shipment?.booking?.numberOfContainers ?? "",
                        countryIdColor: countryColorObj?.color || "",
                        countryIdBackground: countryColorObj?.backgroundColor || "",
                        commercialInvoiceNo: shipment?.commercialInvoiceNo || "",
                        bookingNumber: shipment?.booking?.bookingNumber || "",
                    };
                });
                setData(shipmentData);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setError('No import shipments found');
            }
        } catch (error) {
            setError('Error fetching import shipments');
            console.error('Error fetching import shipments', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, token, colorlist])

    const defaultView = 'importshipmentreport';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("supplierNamesId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.supplierNamesId) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );
    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };
    return (
        <>
            <>
                <CustomReport
                    defaultView={defaultView}
                    mainContent={getMainContent(mainContent, permission)}
                />
                {!permission?.view && <AccessDenied />}
            </>
        </>
    );
};
export default ImportShipmentReport;
